import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import { candidateJobs } from '../../data/observations.ts'
import { useMemberships, usePersons } from '../../data/people.ts'
import { say, useJobAreas, useJobs } from '../../data/plan.ts'
import { assigneeOptions, faultEventsOf, faultState, useFaultEvents, useWater, type FaultState, type RolePermissionRow, type WaterFault } from '../../data/water.ts'
import { localDate, formatLocal, localToInstant } from '../../lib/time.ts'
import { useMerged } from '../../sync/hooks.ts'
import { Banner, Field, PendingBanner, Pill, Section } from '../../ui/components.tsx'
import { useAction, useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { useAreaOptions, usePersonNames } from '../measures/shared.tsx'
import { FAULT_KINDS, useStations } from './shared.tsx'

/** What went wrong with the irrigation: raised in the field by whoever sees it, resolved once by whoever repaired it (and never reopened: a new fault is a new record). A record for the people who repair; the app does not
 *  talk to the irrigation system, so it neither sees its alarms nor clears them.
 *  M16-L1: between its raising and its resolution a fault has a history the team reads on the row — who saw it ("Vu"), who was given it ("Confier à…", somebody who works
 *  on the course at the site), which repair job follows it ("Lier un travail", after an ordinary job the planner creates; "Créer un travail" opens the planner's form) —
 *  from the mirrored events (`ops.irrigation_fault_event`); each act is an intent answered by the server (`useWorkshopAction`), kept on the device when there is no network. */
export function FaultsTab({ autoNew = false }: { /** opened from Today's "Signaler une panne d'arrosage" (`?tab=faults&new=1`): the description takes the focus */ autoNew?: boolean }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const water = useWater()
  const events = useFaultEvents()
  const areas = useAreaOptions(); const stations = useStations()
  const nameOf = usePersonNames()
  const today = localDate(new Date(), site.timezone)
  const [showResolved, setShowResolved] = useState(false)
  const open = useMemo(() => water.faults.filter((f) => !f.resolved_at).sort((a, b) => (a.observed_at < b.observed_at ? -1 : 1)), [water.faults])
  const resolved = useMemo(() => water.faults.filter((f) => f.resolved_at).sort((a, b) => ((a.resolved_at ?? '') < (b.resolved_at ?? '') ? 1 : -1)).slice(0, 30), [water.faults])
  if (!water.ready || !areas || !stations) return <p>{t('common.loading')}</p>
  const row = (f: WaterFault) => {
    const area = areas.find((a) => a.id === f.area_id); const st = stations.find((s) => s.id === f.station_feature_id)
    const age = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${f.observed_local_date}T00:00:00Z`)) / 86_400_000)
    const state = faultState(faultEventsOf(events ?? [], f.id))
    return (
      <li key={f.id} className="card flex flex-col gap-1" data-testid="fault-row" data-id={f.id} data-state={f.resolved_at ? 'resolved' : 'open'}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-semibold">{t(`water.faultKind.${f.kind}`)}{area ? ` · ${area.label}` : ''}{st ? ` · ${st.label}` : ''}</span>
          {!f.resolved_at && <Pill tone={age >= 7 ? 'red' : 'amber'}>{t('water.faults.age', { count: age })}</Pill>}
          {f.resolved_at && <Pill tone="green">{t('water.faults.resolved')}</Pill>}
        </div>
        <p>{f.description}</p>
        <p className="meta">{formatLocal(f.observed_at, site.timezone)}{nameOf(f.actor_person_id) ? ` · ${t('measures.by', { who: nameOf(f.actor_person_id) })}` : ''}{(f as WaterFault)._pending ? ` · ${t('measures.pill.pending')}` : ''}</p>
        <FaultHistory state={state} />
        {f.resolved_at && <p className="meta">{t('water.faults.resolvedOn', { date: formatLocal(f.resolved_at, site.timezone, 'dd/MM/yyyy') })}{f.resolution_note ? ` · ${f.resolution_note}` : ''}</p>}
        {!f.resolved_at && !(f as WaterFault)._pending && <FaultActions f={f} state={state} today={today} />}
        {!f.resolved_at && !(f as WaterFault)._pending && <ResolveForm f={f} />}
      </li>
    )
  }
  return (
    <div className="flex flex-col gap-5" data-testid="water-faults">
      <RaiseForm autoNew={autoNew} />
      <Section title={t('water.faults.open')} testId="faults-open-section">
        {open.length === 0 && <p className="opacity-70" data-testid="faults-none">{t('water.faults.none')}</p>}
        <ul className="flex flex-col gap-2" data-testid="faults-open">{open.map(row)}</ul>
      </Section>
      {resolved.length > 0 && (
        <Section title={t('water.faults.resolvedTitle')} testId="faults-resolved-section">
          <label className="flex min-h-14 items-center gap-3"><input type="checkbox" className="size-6" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="faults-show-resolved" /> {t('water.faults.showResolved', { count: resolved.length })}</label>
          {showResolved && <ul className="flex flex-col gap-2" data-testid="faults-resolved">{resolved.map(row)}</ul>}
        </Section>
      )}
    </div>
  )
}

/** The fault's history on its row: who saw it, who has it, which job follows it (the latest assignment and the latest link are the current ones; the server's rule). */
function FaultHistory({ state }: { state: FaultState }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const nameOf = usePersonNames()
  const jobs = useJobs()
  if (state.seenBy.length === 0 && !state.assignee && !state.job) return null
  const who = (id: string | null) => nameOf(id) ?? t('water.faults.someone')
  const day = (at: string) => formatLocal(at, site.timezone, 'dd/MM/yyyy')
  const job = state.job ? jobs?.find((j) => j.id === state.job!.jobId) : undefined
  return (
    <ul className="flex flex-col gap-1" data-testid="fault-events">
      {state.seenBy.map((s) => <li key={`seen-${s.personId ?? 'x'}`} className="meta" data-testid="fault-event" data-kind="acknowledged">{t('water.faults.seenBy', { who: who(s.personId), date: day(s.at) })}</li>)}
      {state.assignee && <li className="meta" data-testid="fault-event" data-kind="assigned">{t('water.faults.assignedTo', { who: who(state.assignee.personId), date: day(state.assignee.at) })}</li>}
      {state.job && (
        <li className="flex flex-col gap-2" data-testid="fault-event" data-kind="job_linked" data-job={state.job.jobId}>
          <span className="meta">{t('water.faults.jobLinked', { title: job ? say(job.title, i18n.language) || job.task_type_code : t('water.faults.jobUnknown'), date: job ? job.scheduled_local_date.split('-').reverse().join('/') : day(state.job.at) })}</span>
          {/* a 56-px control, as the area page's link to a follow-up job (M23) */}
          <Link to="/plan/job/$jobId" params={{ jobId: state.job.jobId }} className="btn !px-4 self-start" data-testid="fault-job-link">{t('water.faults.jobOpen')}</Link>
        </li>
      )}
    </ul>
  )
}

/** "Vu", "Confier à…", "Lier un travail" / "Créer un travail" on an open fault: whoever repairs sees and gives (water.resolve), whoever plans links (job.plan). */
function FaultActions({ f, state, today }: { f: WaterFault; state: FaultState; today: string }) {
  const { t } = useTranslation()
  const { personId, site } = useClub()
  const resolve = useWriteAccess('water.resolve')
  const plan = useWriteAccess('job.plan')
  if (!resolve.allowed && !plan.allowed) return null
  const seenByMe = !!personId && state.seenBy.some((s) => s.personId === personId)
  return (
    <div className="flex flex-col gap-2" data-testid="fault-actions">
      {resolve.allowed && (
        <div className="flex flex-wrap gap-2">
          {!seenByMe && <AckButton f={f} />}
          <AssignForm f={f} state={state} siteId={site.site_id} />
        </div>
      )}
      {plan.allowed && <LinkJobForm f={f} state={state} today={today} />}
    </div>
  )
}

function AckButton({ f }: { f: WaterFault }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  return (
    <div className="flex flex-col gap-2">
      <button type="button" className="btn !px-4" disabled={act.busy} onClick={() => { void act.run('irrigation.fault_acknowledge@1', { event_id: id.current, fault_id: f.id }, 'water.faults.ackDone').then((ok) => { if (ok) id.current = newId() }) }} data-testid="fault-ack">{t('water.faults.ack')}</button>
      {act.pending && <PendingBanner />}
      {act.error && <Banner tone="error"><span data-testid="fault-ack-error">{act.error}</span></Banner>}
    </div>
  )
}

function AssignForm({ f, state, siteId }: { f: WaterFault; state: FaultState; siteId: string }) {
  const { t } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  const persons = usePersons(); const memberships = useMemberships()
  const rolePermissions = useMerged<RolePermissionRow>('role_permission', 'core.role_permission')
  const options = useMemo(() => assigneeOptions(persons ?? [], memberships ?? [], rolePermissions ?? [], siteId), [persons, memberships, rolePermissions, siteId])
  const [open, setOpen] = useState(false)
  const [who, setWho] = useState('')
  return (
    <div className="flex flex-col gap-2">
      <button type="button" className="btn !px-4" onClick={() => setOpen((x) => !x)} data-testid="fault-assign">{t('water.faults.assignGo')}</button>
      {open && (
        <form className="flex flex-col gap-3 border-t pt-3" data-testid="fault-assign-form" onSubmit={(e) => {
          e.preventDefault()
          if (who) void act.run('irrigation.fault_assign@1', { event_id: id.current, fault_id: f.id, person_id: who }, 'water.faults.assignedDone').then((ok) => { if (ok) { id.current = newId(); setOpen(false) } })
        }}>
          {options.length === 0 ? <p className="meta" data-testid="fault-assign-none">{t('water.faults.assignNone')}</p> : (
            <Field label={t('water.faults.assignTo')} htmlFor={`as-${f.id}`}>
              <select id={`as-${f.id}`} className="input" value={who} onChange={(e) => setWho(e.target.value)} data-testid="fault-assign-person">
                <option value="">{t('mounts.choose')}</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.name}{state.assignee?.personId === o.id ? ` · ${t('water.faults.assignCurrent')}` : ''}</option>)}
              </select>
            </Field>
          )}
          {act.pending && <PendingBanner />}
          {act.error && <Banner tone="error"><span data-testid="fault-assign-error">{act.error}</span></Banner>}
          <button type="submit" className="btn btn-primary" disabled={act.busy || !who} data-testid="fault-assign-save">{t('water.faults.assignSave')}</button>
        </form>
      )}
    </div>
  )
}

function LinkJobForm({ f, state, today }: { f: WaterFault; state: FaultState; today: string }) {
  const { t, i18n } = useTranslation()
  const act = useWorkshopAction()
  const id = useRef(newId())
  const jobs = useJobs(); const jobAreas = useJobAreas()
  // the site's jobs not cancelled, planned on or after the day the fault was seen, the ones on its area first (N's rule for an observation's follow-up; a fault with no area has none "on it")
  const candidates = useMemo(() => candidateJobs(jobs ?? [], jobAreas ?? [], f.area_id ?? '', f.observed_local_date, (j) => say(j.title, i18n.language) || j.task_type_code), [jobs, jobAreas, f.area_id, f.observed_local_date, i18n.language])
  const [open, setOpen] = useState(false)
  const [jobId, setJobId] = useState('')
  return (
    <div className="flex flex-col gap-2">
      <button type="button" className="btn !px-4" onClick={() => setOpen((x) => !x)} data-testid="fault-link">{t('water.faults.linkGo')}</button>
      {open && (
        <form className="flex flex-col gap-3 border-t pt-3" data-testid="fault-link-form" onSubmit={(e) => {
          e.preventDefault()
          if (jobId) void act.run('irrigation.fault_link_job@1', { event_id: id.current, fault_id: f.id, job_id: jobId }, 'water.faults.linkedDone').then((ok) => { if (ok) { id.current = newId(); setOpen(false) } })
        }}>
          <p className="meta">{t('water.faults.linkHelp')}</p>
          <Link to="/plan/new" search={{ d: today, ...(f.area_id ? { area: f.area_id } : {}) }} className="btn" data-testid="fault-create-job">{t('water.faults.createJob')}</Link>
          {candidates.length === 0 ? <p className="meta" data-testid="fault-link-none">{t('water.faults.linkNone')}</p> : (
            <Field label={t('water.faults.linkPick')} htmlFor={`lj-${f.id}`}>
              <select id={`lj-${f.id}`} className="input" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="fault-link-job">
                <option value="">{t('mounts.choose')}</option>
                {candidates.map((j) => <option key={j.id} value={j.id}>{`${j.date.split('-').reverse().join('/')} · ${j.title}${j.onArea ? ` · ${t('water.faults.linkOnArea')}` : ''}${state.job?.jobId === j.id ? ` · ${t('water.faults.linkCurrent')}` : ''}`}</option>)}
              </select>
            </Field>
          )}
          {act.pending && <PendingBanner />}
          {act.error && <Banner tone="error"><span data-testid="fault-link-error">{act.error}</span></Banner>}
          <button type="submit" className="btn btn-primary" disabled={act.busy || !jobId} data-testid="fault-link-save">{t('water.faults.linkSave')}</button>
        </form>
      )}
    </div>
  )
}

function RaiseForm({ autoNew }: { autoNew: boolean }) {
  const { t } = useTranslation()
  const { send, site } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('water.record')
  const areas = useAreaOptions(); const stations = useStations()
  const { busy, error, run } = useAction()
  const id = useRef(newId())
  const descriptionRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (autoNew) { descriptionRef.current?.scrollIntoView?.({ block: 'center' }); descriptionRef.current?.focus() } }, [autoNew])
  const [kind, setKind] = useState<(typeof FAULT_KINDS)[number]>('leak'); const [areaId, setAreaId] = useState(''); const [stationId, setStationId] = useState(''); const [description, setDescription] = useState(''); const [when, setWhen] = useState('')
  const [done, setDone] = useState<string | null>(null)
  if (!access.permitted) return null
  function submit(e: FormEvent) {
    e.preventDefault(); setDone(null)
    void run(async () => {
      const at = when ? localToInstant(when.slice(0, 10), when.slice(11, 16), site.timezone) : undefined
      await send('irrigation.fault_raise@1', { fault_id: id.current, kind, description: description.trim(), ...(areaId ? { area_id: areaId } : {}), ...(stationId ? { station_id: stationId } : {}), ...(at ? { observed_at: at } : {}) })
      id.current = newId(); setDescription(''); setWhen('')
      setDone(engine.online === false || !navigator.onLine ? t('water.faults.raisedOffline') : t('water.faults.raised'))
    })
  }
  return (
    <Section title={t('water.faults.raise')} help={t('water.faults.help')} testId="fault-raise">
      <form className="card flex flex-col gap-3" onSubmit={submit} data-testid="fault-form">
        <Field label={t('water.faults.kind')} htmlFor="f-kind"><select id="f-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} data-testid="fault-kind">{FAULT_KINDS.map((k) => <option key={k} value={k}>{t(`water.faultKind.${k}`)}</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('measures.areaOptional')} htmlFor="f-area"><select id="f-area" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="fault-area"><option value="">{t('measures.import.none')}</option>{areas?.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</select></Field>
          <Field label={t('water.runs.station')} htmlFor="f-station"><select id="f-station" className="input" value={stationId} onChange={(e) => setStationId(e.target.value)} data-testid="fault-station"><option value="">{t('measures.import.none')}</option>{stations?.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></Field>
        </div>
        <Field label={t('water.faults.description')} htmlFor="f-desc"><input ref={descriptionRef} id="f-desc" className="input" maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="fault-description" /></Field>
        <Field label={t('water.faults.when')} hint={t('measures.whenHint')} htmlFor="f-when"><input id="f-when" className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="fault-when" /></Field>
        {error && <Banner tone="error"><span data-testid="fault-error">{error}</span></Banner>}
        {done && <Banner tone="ok"><span data-testid="fault-done">{done}</span></Banner>}
        <button className="btn btn-primary" disabled={busy || access.readOnlyMode || description.trim() === ''} data-testid="fault-save">{t('water.faults.save')}</button>
      </form>
    </Section>
  )
}

function ResolveForm({ f }: { f: WaterFault }) {
  const { t } = useTranslation()
  const access = useWriteAccess('water.resolve')
  const act = useWorkshopAction()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  if (!access.allowed) return null
  return (
    <div>
      <button className="btn !px-4" onClick={() => setOpen((x) => !x)} data-testid="fault-resolve">{t('water.faults.resolveGo')}</button>
      {open && (
        <form className="mt-2 flex flex-col gap-3 border-t pt-3" data-testid="fault-resolve-form" onSubmit={(e) => { e.preventDefault(); void act.run('irrigation.fault_resolve@1', { fault_id: f.id, ...(note.trim() ? { note: note.trim() } : {}) }, 'water.faults.resolvedDone') }}>
          <Field label={t('water.faults.resolutionNote')} htmlFor={`rn-${f.id}`}><input id={`rn-${f.id}`} className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="fault-resolve-note" /></Field>
          {act.pending && <PendingBanner />}
          {act.error && <Banner tone="error"><span data-testid="fault-resolve-error">{act.error}</span></Banner>}
          <button className="btn btn-primary" disabled={act.busy} data-testid="fault-resolve-save">{t('water.faults.resolveSave')}</button>
        </form>
      )}
    </div>
  )
}
